import type { RefObject } from "react";
import StoryboardList from "@/components/storyboard/StoryboardList";
import { GlossaryHighlightProvider } from "@/components/glossary/GlossaryHighlightContext";
import PageHeadline from "@/components/course/PageHeadline";
import { SectionContent } from "@/components/course/SectionContent";
import {
  useChapterOutroReveal,
  type OutroEntryMode,
} from "@/hooks/course/useChapterOutroReveal";
import { useSyncOutroTocFocus } from "@/hooks/course/useSyncOutroTocFocus";
import { cn } from "@/lib/utils";
import type { CourseTreeNode } from "@/types/generated";
import type { Storyboard } from "@/types/storyboard";
import type { GlossaryTerm } from "@/types/glossary";

interface ChapterOutroScrollProps {
  initialMode: OutroEntryMode;
  title: string;
  body: string;
  ctaLabel: string;
  onNext: () => void;
  onOutroSeen?: () => void;
  contentTitle?: string;
  contentElements: CourseTreeNode[];
  storyboards: Storyboard[];
  storyboardsLoading: boolean;
  glossaryTerms?: GlossaryTerm[];
  className?: string;
}

interface LastSubchapterBandProps {
  contentRef: RefObject<HTMLDivElement | null>;
  contentLocked: boolean;
  contentTitle?: string;
  contentElements: CourseTreeNode[];
  storyboards: Storyboard[];
  storyboardsLoading: boolean;
  glossaryTerms: GlossaryTerm[];
}

/**
 * Last-subchapter band — clear while finishing the lesson, blurred when Abschluss is active.
 */
const LastSubchapterBand = ({
  contentRef,
  contentLocked,
  contentTitle,
  contentElements,
  storyboards,
  storyboardsLoading,
  glossaryTerms,
}: LastSubchapterBandProps) => (
  <div
    ref={contentRef}
    className={cn(
      "relative flex flex-col bg-background px-10 pt-8 pb-1 transition-[filter,opacity] duration-500 motion-reduce:transition-none [overflow-anchor:none]",
      contentLocked && "pointer-events-none opacity-70 blur-[3px] saturate-75"
    )}
    inert={contentLocked ? true : undefined}
  >
    {contentTitle && (
      <PageHeadline
        title={contentTitle}
        level="h2"
        headingClassName="text-heading-h1"
        className="mb-8"
      />
    )}

    <GlossaryHighlightProvider
      terms={glossaryTerms}
      resetKey={contentTitle ?? "outro-content"}
    >
      <StoryboardList
        loading={storyboardsLoading}
        storyboards={storyboards}
        selectedContentElements={contentElements}
      />
    </GlossaryHighlightProvider>

    {contentLocked && (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-background to-transparent"
      />
    )}
  </div>
);

interface OutroBandProps {
  outroRef: RefObject<HTMLDivElement | null>;
  outroHeadingRef: RefObject<HTMLHeadingElement | null>;
  outroLocked: boolean;
  title: string;
  body: string;
  ctaLabel: string;
  onNext?: () => void;
}

/**
 * Chapter Abschluss band — locked until unveiled.
 */
const OutroBand = ({
  outroRef,
  outroHeadingRef,
  outroLocked,
  title,
  body,
  ctaLabel,
  onNext,
}: OutroBandProps) => (
  <div
    ref={outroRef}
    className={cn(
      "relative flex flex-col bg-background px-10 pt-8 transition-[filter,opacity] duration-500 motion-reduce:transition-none [overflow-anchor:none]",
      outroLocked && "pointer-events-none opacity-70 blur-[3px] saturate-75"
    )}
    inert={outroLocked ? true : undefined}
  >
    <SectionContent
      title={title}
      body={body}
      ctaLabel={ctaLabel}
      onStart={onNext}
      headingRef={outroHeadingRef}
    />

    {outroLocked && (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-linear-to-b from-background to-transparent"
      />
    )}
  </div>
);

/**
 * Chapter outro: last-subchapter content stacked above Abschluss.
 */
const ChapterOutroScroll = ({
  initialMode,
  title,
  body,
  ctaLabel,
  onNext,
  onOutroSeen,
  contentTitle,
  contentElements,
  storyboards,
  storyboardsLoading,
  glossaryTerms = [],
  className,
}: ChapterOutroScrollProps) => {
  const { rootRef, contentRef, outroRef, outroHeadingRef, outroActive } =
    useChapterOutroReveal(initialMode, onOutroSeen);
  useSyncOutroTocFocus(outroActive);

  const hasContent =
    storyboardsLoading || contentElements.length > 0 || storyboards.length > 0;

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex min-h-full flex-col",
        "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-4 motion-safe:duration-500",
        className
      )}
    >
      {hasContent && (
        <LastSubchapterBand
          contentRef={contentRef}
          contentLocked={outroActive}
          contentTitle={contentTitle}
          contentElements={contentElements}
          storyboards={storyboards}
          storyboardsLoading={storyboardsLoading}
          glossaryTerms={glossaryTerms}
        />
      )}

      <OutroBand
        outroRef={outroRef}
        outroHeadingRef={outroHeadingRef}
        outroLocked={!outroActive}
        title={title}
        body={body}
        ctaLabel={ctaLabel}
        onNext={onNext}
      />
    </div>
  );
};

export default ChapterOutroScroll;
